import { useMemo } from 'react';
import { User } from '@auth/user';
import useAuth from '@fuse/core/FuseAuthProvider/useAuth';
import isEqual from 'lodash/isEqual';
import setIn from '@/utils/setIn';
import { setLocalStorage } from '@/utils/localStorage';

type useUser = {
	data: User | null;
	isGuest: boolean;
	updateUser: (updates: Partial<User>) => Promise<User | undefined>;
	updateUserSettings: (newSettings: User['settings']) => Promise<User['settings'] | undefined>;
	signOut: () => void;
};

function useUser(): useUser {

	const { authState, signOut } = useAuth();

	/**
	 * Get user from auth state
	 */
	const user = authState?.user as User | null;

	/**
	 * Check if user is guest
	 */
	const isGuest = useMemo(() => !user?.roles || user?.roles?.length === 0, [user]);

	/**
	 * Update user
	 * Uses current auth provider's updateUser method
	 */
	async function handleUpdateUser(_data: Partial<User>) {
		// need some logic in here to update user
		setLocalStorage('theme', _data?.settings);

		return _data as User;
	}

	/**
	 * Update user settings
	 * Uses current auth provider's updateUser method
	 */
	async function handleUpdateUserSettings(newSettings: User['settings']) {
		const newUser = setIn(user, 'settings', newSettings) as User;

		setLocalStorage('theme', user?.settings);

		if (isEqual(user, newUser)) {
			return undefined;
		}

		const updatedUser = await handleUpdateUser(newUser);

		return updatedUser?.settings;
	}

	/**
	 * Sign out
	 */
	async function handleSignOut() {
		return signOut();
	}

	return {
		data: user,
		isGuest,
		signOut: handleSignOut,
		updateUser: handleUpdateUser,
		updateUserSettings: handleUpdateUserSettings,
	};
}

export default useUser;